import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Sun } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Seo } from "@/components/Seo";

export default function Login() {
  const { login } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setLoading(true);
    const res = await login(email, password);
    setLoading(false);
    if (res.ok) {
      toast.success("Welcome back!");
      nav("/dashboard");
    } else {
      setErr(res.error);
    }
  };

  return (
    <div className="min-h-screen grid md:grid-cols-2">
      <Seo
        title="Log in"
        description="Log in to HomeSchool Hub — the all-in-one platform for Florida homeschool teachers."
        path="/login"
        noindex
      />
      <div className="hidden md:block relative">
        <img src="https://images.unsplash.com/photo-1770003354577-82b2a2d09d19" alt="" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-br from-amber-500/70 via-orange-400/40 to-transparent" />
        <div className="relative p-12 text-white h-full flex flex-col justify-between">
          <Link to="/" className="flex items-center gap-2 font-display font-bold text-xl" data-testid="brand-link">
            <span className="inline-grid place-items-center w-9 h-9 rounded-2xl bg-white text-amber-500"><Sun className="w-5 h-5" /></span>
            HomeSchool Hub
          </Link>
          <div>
            <div className="font-display text-3xl font-semibold leading-tight max-w-md">
              "It finally feels like I have a co-planner in the room."
            </div>
            <div className="mt-3 text-white/80">— Amy, homeschool mom of 3 in Tampa</div>
          </div>
        </div>
      </div>

      <div className="flex items-center justify-center p-8">
        <div className="w-full max-w-md">
          <h1 className="font-display text-3xl font-bold tracking-tight">Welcome back</h1>
          <p className="mt-2 text-stone-500">Log in to plan your week.</p>

          <form onSubmit={submit} className="mt-8 space-y-5" data-testid="login-form">
            <div>
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="mt-2 rounded-xl h-12"
                data-testid="login-email-input"
              />
            </div>
            <div>
              <div className="flex items-center justify-between">
                <Label htmlFor="password">Password</Label>
                <Link to="/forgot-password" className="text-xs text-amber-700 hover:text-amber-900 font-medium" data-testid="login-forgot-password-link">
                  Forgot password?
                </Link>
              </div>
              <Input
                id="password"
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="mt-2 rounded-xl h-12"
                data-testid="login-password-input"
              />
            </div>
            {err && (
              <div className="text-sm text-red-600 bg-red-50 border border-red-100 rounded-lg px-3 py-2" data-testid="login-error">
                {err}
              </div>
            )}
            <Button type="submit" disabled={loading} className="w-full h-12 rounded-full bg-amber-500 hover:bg-amber-600 text-white text-base" data-testid="login-submit">
              {loading ? "Signing in…" : "Sign in"}
            </Button>
          </form>

          <div className="mt-6 text-sm text-stone-500">
            New here?{" "}
            <Link to="/register" className="text-amber-600 hover:underline font-medium" data-testid="login-to-register">
              Create an account
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
